import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Netzbetreiber, UniqueTeilgebietAndNetzbetreibernummer } from '../../domain/model/netzbetreiber';
import { map, shareReplay } from 'rxjs/operators';
import { NetzzuordnungSnapshot } from '../../domain/model/netzzuordnung-snapshot';

@Injectable({
    providedIn: 'root',
})
export class NetzbetreiberService {
    public refreshRequested$ = new Subject<void>();

    private readonly VALID_CACHE_TIME: number = 1000 * 5; // 5 seconds
    private readonly CACHE_SIZE = 1;

    private cache$: Observable<Netzbetreiber[]>;
    private CACHE_CALLED: number = 0;

    private readonly SERVICE_URL = `${environment.API_URL}/netzbetreiber`;
    private readonly SERVICE_URL_ADMIN = `${environment.API_URL}/admin/netzbetreiber`;

    private http = inject(HttpClient);

    public getAlleNetzbetreiber(): Observable<Netzbetreiber[]> {
        return this.http.get<Netzbetreiber[]>(this.SERVICE_URL);
    }

    public getCachedNetzbetreiber(): Observable<Netzbetreiber[]> {
        const now = Date.now();
        if (!this.cache$ || now - this.CACHE_CALLED > this.VALID_CACHE_TIME) {
            this.CACHE_CALLED = now;
            this.cache$ = this.getAlleNetzbetreiber().pipe(
                shareReplay(this.CACHE_SIZE),
            );
        }

        return this.cache$;
    }

    public getAlleNetzbetreiberNamen(name?: string): Observable<string[]> {
        return this.getAlleNetzbetreiber().pipe(map((nb: Netzbetreiber[]) => {
            return [
                ...new Set(nb.filter(value => !name || value.name?.toLowerCase()?.indexOf(name.toLowerCase()) > -1)
                    .map(value => value.name)
                    .filter(option => option),
                ),
            ];
        },
        ));
    }

    public getAlleNetzbetreiberIds(stadtwerkeId: string): Observable<string[]> {
        return this.getAlleNetzbetreiber().pipe(map((nb: Netzbetreiber[]) => {
            return [
                ...new Set(nb.filter(value => !stadtwerkeId || value.stadtwerkeId?.toLowerCase()?.indexOf(stadtwerkeId.toLowerCase()) > -1)
                    .map(value => value.stadtwerkeId)
                    .filter(option => option),
                ),
            ];
        },
        ));
    }

    public getAlleTeilgebiete(tgb: string): Observable<string[]> {
        return this.getAlleNetzbetreiber().pipe(map((nb: Netzbetreiber[]) => {
            return [
                ...new Set(nb.filter(value => !tgb || value.teilgebiet?.toLowerCase()?.indexOf(tgb.toLowerCase()) > -1)
                    .map(value => value.teilgebiet)
                    .filter(option => option),
                ),
            ];
        },
        ));
    }

    public getAlleUniqueTeilgebietAndNetzbetreibernummer(unique: UniqueTeilgebietAndNetzbetreibernummer): Observable<UniqueTeilgebietAndNetzbetreibernummer[]> {
        return this.getCachedNetzbetreiber().pipe(
            map((nb: Netzbetreiber[]) => {
                return nb.filter(value => {
                    return value.teilgebiet?.trim()?.toLowerCase() === unique.teilgebiet?.trim()?.toLowerCase() &&
                        value.netzbetreibernummer?.trim()?.toLowerCase() === unique.netzbetreibernummer?.trim()?.toLowerCase();
                });
            }),
        );
    }

    public getAlleNetzbetreiberNummern(netzbetreibernummer: string): Observable<string[]> {
        return this.getAlleNetzbetreiber().pipe(map((nb: Netzbetreiber[]) => {
            return [
                ...new Set(nb.filter(value => !netzbetreibernummer || value.netzbetreibernummer?.indexOf(netzbetreibernummer) > -1)
                    .map(value => value.netzbetreibernummer),
                ),
            ];
        },
        ));
    }

    public getAlleGeschaeftspartnerIds(geschaeftspartnerid: string): Observable<string[]> {
        return this.getAlleNetzbetreiber().pipe(map((nb: Netzbetreiber[]) => {
            return [
                ...new Set(nb.filter(value => !geschaeftspartnerid || value.geschaeftspartnerId?.toLowerCase().indexOf(geschaeftspartnerid.toLowerCase()) > -1)
                    .map(value => value.geschaeftspartnerId)
                    .filter(option => option),
                ),
            ];
        },
        ));
    }

    public getNetzbetreiber(stadtwerkeId: string): Observable<Netzbetreiber> {
        return this.http.get<Netzbetreiber>(`${this.SERVICE_URL}/${stadtwerkeId}`);
    }

    public saveNetzbetreiberKontaktdaten(netzbetreiber: Netzbetreiber): Observable<Netzbetreiber> {
        return this.http.put<Netzbetreiber>(
            `${this.SERVICE_URL}/${netzbetreiber.stadtwerkeId}/kontaktdaten`,
            netzbetreiber,
        );
    }

    public createNetzbetreiber(netzbetreiber: Netzbetreiber): Observable<Netzbetreiber> {
        return this.http.post<Netzbetreiber>(
            this.SERVICE_URL_ADMIN,
            netzbetreiber,
        );
    }

    public updateLastUuidChangeConfirmDate(stwId: string, lastUuidChangeConfirmDate: Date): Observable<NetzzuordnungSnapshot> {
        return this.http.post<NetzzuordnungSnapshot>(
            `${this.SERVICE_URL}/${stwId}/updateLastUuidChangeConfirmDate`,
            lastUuidChangeConfirmDate,
        );
    }


    public saveNetzbetreiberBasisdaten(netzbetreiber: Netzbetreiber): Observable<Netzbetreiber> {
        return this.http.put<Netzbetreiber>(
            `${this.SERVICE_URL_ADMIN}/${netzbetreiber.stadtwerkeId}/basisdaten`,
            netzbetreiber,
        );
    }

    public deleteNetzbetreiber(netzbetreiber: Netzbetreiber): Observable<Netzbetreiber> {
        return this.http.delete<Netzbetreiber>(`${this.SERVICE_URL_ADMIN}/${netzbetreiber.stadtwerkeId}`);
    }

    public isStadtwerkeIdAvailable(stadtwerkeId: string): Observable<boolean> {
        return this.http.get<boolean>(`${this.SERVICE_URL_ADMIN}/stadtwerkeid/${stadtwerkeId}`);
    }

    public getAvailableStadtwerkeId(): Observable<string> {
        return this.http.get(
            `${this.SERVICE_URL_ADMIN}/available_stadtwerkeid`,
            { responseType: 'text' },
        ) as Observable<string>;
    }
}
